import { faEllipsisVertical, faEye, faLink, faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@components/ui/dropdown-menu';
import { usePermissions } from '@shared/hooks/usePermissions';

import { Pack } from '../../models/Pack';

interface DropdownActionsProps {
  pack: Pack;
  onView?: (pack: Pack) => void;
  onEdit?: (pack: Pack) => void;
  onLink?: (pack: Pack) => void;
  onDelete?: (pack: Pack) => void;
}

export const DropdownActions = ({ pack, onView, onEdit, onLink, onDelete }: DropdownActionsProps) => {
  // 🎯 Hook de traducciones
  const { t } = useTranslation('packsModule');
  // 🎯 Hook de permisos
  const { canAccess } = usePermissions();

  // 🎯 Verificar permisos para editar y enlazar (tenant, tenant_3pl y tenant_external)
  const canEdit = canAccess(['tenant', 'tenant_3pl', 'tenant_external'], ['packs.edit']);
  const canLink = canAccess(['tenant', 'tenant_3pl', 'tenant_external'], ['packs.edit']);
  const canDeletePack = canAccess(['tenant', 'tenant_3pl', 'tenant_external'], ['packs.delete']);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="hover:bg-muted rounded-md p-1"
          data-testid={`dropdown-trigger-${pack.id}`}
          onClick={(e) => e.stopPropagation()} // Evitar que se propague al card
        >
          <FontAwesomeIcon icon={faEllipsisVertical} className="text-text" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-44 border-card-border" data-testid={`dropdown-content-${pack.id}`}>
        {onView && (
          <DropdownMenuItem
            data-testid={`dropdown-view-${pack.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onView(pack);
            }}
          >
            <FontAwesomeIcon icon={faEye} className="mr-2 w-3.5" />
            {t('button.view')}
          </DropdownMenuItem>
        )}

        {onEdit && canEdit && (
          <DropdownMenuItem
            data-testid={`dropdown-edit-${pack.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(pack);
            }}
          >
            <FontAwesomeIcon icon={faPen} className="mr-2 w-3.5" />
            {t('button.edit')}
          </DropdownMenuItem>
        )}

        {onLink && canLink && (
          <DropdownMenuItem
            data-testid={`dropdown-link-${pack.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onLink(pack);
            }}
          >
            <FontAwesomeIcon icon={faLink} className="mr-2 w-3.5" />
            {t('button.link')}
          </DropdownMenuItem>
        )}

        {onDelete && canDeletePack && (
          <DropdownMenuItem
            data-testid={`dropdown-delete-${pack.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(pack);
            }}
          >
            <FontAwesomeIcon icon={faTrash} className="mr-2 w-3.5" />
            {t('button.delete')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
